import type { DynamicGridItem } from '@cast/design-system';

import type {
  WorkloadCategory,
  WorkloadCategoryFilterOption,
} from '../../_constants';
import { WORKLOAD_CATEGORIES } from '../../_constants';

export type ItemCategoryMap = Record<
  string,
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.DEFAULT>
>;

export const matchesSearch = (
  item: DynamicGridItem,
  search: string
): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return (
    item.title.toLowerCase().includes(query) ||
    item.description.toLowerCase().includes(query)
  );
};

export const matchesCategories = (
  itemId: string,
  selectedCategories: WorkloadCategory[],
  itemCategories: ItemCategoryMap
): boolean => {
  if (!selectedCategories.length) return true;
  const category = itemCategories[itemId];
  return category != null && selectedCategories.includes(category);
};

type FilterCustomizeDrawerItemsArgs = {
  items: DynamicGridItem[];
  search: string;
  selectedCategories: WorkloadCategory[];
  itemCategories: ItemCategoryMap;
};

export const filterCustomizeDrawerItems = ({
  items,
  search,
  selectedCategories,
  itemCategories,
}: FilterCustomizeDrawerItemsArgs): DynamicGridItem[] =>
  items.filter(
    (item) =>
      matchesSearch(item, search) &&
      matchesCategories(item.id, selectedCategories, itemCategories)
  );

export type CategorizedItemGroup = {
  category: WorkloadCategoryFilterOption['value'];
  label: string;
  items: DynamicGridItem[];
};

export const groupItemsByCategory = (
  items: DynamicGridItem[],
  itemCategories: ItemCategoryMap,
  categoryOptions: WorkloadCategoryFilterOption[]
): CategorizedItemGroup[] => {
  const byCategory = new Map<string, DynamicGridItem[]>();

  for (const item of items) {
    const category = itemCategories[item.id];
    if (!category) continue;
    const list = byCategory.get(category) ?? [];
    list.push(item);
    byCategory.set(category, list);
  }

  return categoryOptions
    .map((option) => ({
      category: option.value,
      label: option.label,
      items: byCategory.get(option.value) ?? [],
    }))
    .filter((group) => group.items.length > 0);
};
